import type {
  CreatePaymentTermRequest,
  PaymentTermResponse,
  UpdatePaymentTermRequest,
} from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * MD-09 Payment terms — the query hooks. One list key; every mutation
 * invalidates it, and the partner screens read the same list for their picker
 * (a term is ONE record, so a rename shows everywhere at once).
 */
export const PAYMENT_TERMS_KEY = ['payment-terms'] as const;

export function usePaymentTerms(includeInactive = false) {
  return useQuery({
    queryKey: [...PAYMENT_TERMS_KEY, { includeInactive }],
    queryFn: async (): Promise<PaymentTermResponse[]> => {
      const response = await api.paymentTerms.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.terms;
    },
  });
}

export function usePaymentTermMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: PAYMENT_TERMS_KEY });
  const create = useMutation({
    mutationFn: async (body: CreatePaymentTermRequest): Promise<PaymentTermResponse> => {
      const response = await api.paymentTerms.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ termId, body }: { termId: string; body: UpdatePaymentTermRequest }) => {
      const response = await api.paymentTerms.update({ params: { termId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ termId, active }: { termId: string; active: boolean }) => {
      const response = active
        ? await api.paymentTerms.reactivate({ params: { termId }, body: {} })
        : await api.paymentTerms.deactivate({ params: { termId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async ({ termId }: { termId: string }) => {
      const response = await api.paymentTerms.remove({ params: { termId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, remove };
}
